/**
 * Cascade Challenge Page
 * Multi-objection training mode - handle 3 objections back-to-back
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import lumoTechniquesService from '../../services/lumoTechniquesService';
import NavigationBar from '../../components/ui/NavigationBar';
import LoadingState from '../../components/ui/LoadingState';
import TechniqueDetectionBadge from '../../components/TechniqueDetectionBadge';
import Icon from '../../components/AppIcon';
import { cn } from '../../utils/cn';

const INDUSTRIES = [
  { value: 'saas', label: 'SaaS', icon: 'Monitor' },
  { value: 'retail', label: 'Retail', icon: 'ShoppingCart' },
  { value: 'healthcare', label: 'Healthcare', icon: 'Heart' },
  { value: 'finance', label: 'Finance', icon: 'DollarSign' }
];

const DIFFICULTY_LEVELS = [
  { value: 'beginner', label: 'Beginner', color: 'text-green-700 bg-green-100' },
  { value: 'intermediate', label: 'Intermediate', color: 'text-yellow-700 bg-yellow-100' },
  { value: 'advanced', label: 'Advanced', color: 'text-red-700 bg-red-100' }
];

const CascadeChallenge = () => {
  const navigate = useNavigate();
  const { user, profile } = useAuth();

  const [selectedIndustry, setSelectedIndustry] = useState('saas');
  const [selectedDifficulty, setSelectedDifficulty] = useState('intermediate');
  const [scenario, setScenario] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentObjection, setCurrentObjection] = useState(1); // 1, 2, or 3
  const [responses, setResponses] = useState({ 1: '', 2: '', 3: '' });
  const [detectedTechniques, setDetectedTechniques] = useState({ 1: null, 2: null, 3: null });
  const [executionScores, setExecutionScores] = useState({ 1: null, 2: null, 3: null });
  const [isComplete, setIsComplete] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [totalTime, setTotalTime] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
  }, [user, navigate]);

  const loadScenario = async () => {
    setIsLoading(true);
    setCurrentObjection(1);
    setResponses({ 1: '', 2: '', 3: '' });
    setDetectedTechniques({ 1: null, 2: null, 3: null });
    setExecutionScores({ 1: null, 2: null, 3: null });
    setIsComplete(false);
    setStartTime(Date.now());
    setTotalTime(null);

    try {
      const challenge = await lumoTechniquesService.getCascadeChallenge(
        selectedIndustry, 
        selectedDifficulty
      );
      setScenario(challenge);
    } catch (error) {
      console.error('Error loading cascade challenge:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResponseChange = (objectionNum, value) => {
    setResponses(prev => ({ ...prev, [objectionNum]: value }));
  };

  const handleSubmitResponse = async (objectionNum) => {
    const response = responses[objectionNum];
    if (!response || response.trim().length === 0) return;

    // Get the objection text
    const objectionText = objectionNum === 1 ? scenario.objection1 
      : objectionNum === 2 ? scenario.objection2 
      : scenario.objection3;

    // Detect technique used
    const detection = lumoTechniquesService.detectTechniqueUsed(response, objectionText);
    setDetectedTechniques(prev => ({ ...prev, [objectionNum]: detection }));

    // If technique detected, score it
    if (detection?.techniqueSlug) {
      const technique = await lumoTechniquesService.getTechniqueBySlug(detection.techniqueSlug);
      if (technique) {
        const score = lumoTechniquesService.scoreTechniqueExecution(technique, response, {});
        setExecutionScores(prev => ({ ...prev, [objectionNum]: score }));

        // Track application
        try {
          await lumoTechniquesService.trackTechniqueApplication({
            userId: user.id,
            techniqueId: technique.id,
            sessionId: scenario.id,
            sessionType: 'cascade_challenge',
            objectionFaced: objectionText,
            appliedCorrectly: score >= 0.7,
            executionScore: score,
            timeToExecute: null,
            outcome: score >= 0.7 ? 'overcome' : 'partial_overcome',
            whatWorked: score >= 0.7 ? 'Good technique execution' : '',
            whatNeedsWork: score < 0.7 ? 'Technique needs refinement' : ''
          });
        } catch (trackError) {
          console.error('⚠️ Technique tracking failed:', trackError);
        }
      }
    }

    // Move to next objection or complete
    if (objectionNum < 3) {
      setCurrentObjection(objectionNum + 1);
    } else {
      setIsComplete(true);
      setTotalTime(Math.floor((Date.now() - startTime) / 1000));
    }
  };

  const calculateOverallScore = () => {
    const scores = Object.values(executionScores).filter(s => s !== null);
    if (scores.length === 0) return 0;
    const avg = scores.reduce((sum, s) => sum + s, 0) / scores.length;
    return Math.round(avg * 100);
  };

  const getRecommendedTechnique = (objectionNum) => {
    if (objectionNum === 1) return scenario?.recommendedTechnique1;
    if (objectionNum === 2) return scenario?.recommendedTechnique2;
    return scenario?.recommendedTechnique3;
  };

  if (isLoading) {
    return <LoadingState message="Loading cascade challenge..." />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <NavigationBar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 bg-red-100 rounded-xl">
              <Icon name="Flame" className="w-8 h-8 text-red-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Cascade Challenge</h1>
              <p className="text-gray-600">Handle 3 objections back-to-back without breaking</p>
            </div>
          </div>
        </div>

        {/* Setup: Industry & Difficulty Selection */}
        {!scenario && (
          <div className="space-y-6">
            {/* Industry selection */}
            <div className="bg-white p-6 rounded-xl border-2 border-gray-200">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Select Industry</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {INDUSTRIES.map(industry => (
                  <button
                    key={industry.value}
                    onClick={() => setSelectedIndustry(industry.value)}
                    className={cn(
                      'p-4 rounded-xl border-2 transition-all',
                      selectedIndustry === industry.value
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    )}
                  >
                    <Icon name={industry.icon} className={cn(
                      'w-8 h-8 mx-auto mb-2',
                      selectedIndustry === industry.value ? 'text-primary-600' : 'text-gray-400'
                    )} />
                    <p className={cn(
                      'text-sm font-semibold text-center',
                      selectedIndustry === industry.value ? 'text-primary-900' : 'text-gray-700'
                    )}>
                      {industry.label}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            {/* Difficulty selection */}
            <div className="bg-white p-6 rounded-xl border-2 border-gray-200">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Select Difficulty</h3>
              <div className="grid grid-cols-3 gap-4">
                {DIFFICULTY_LEVELS.map(level => (
                  <button
                    key={level.value}
                    onClick={() => setSelectedDifficulty(level.value)}
                    className={cn(
                      'p-4 rounded-xl border-2 transition-all',
                      selectedDifficulty === level.value
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    )}
                  >
                    <p className={cn(
                      'text-sm font-bold text-center',
                      selectedDifficulty === level.value ? 'text-primary-900' : 'text-gray-700'
                    )}>
                      {level.label}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            {/* Start button */}
            <button
              onClick={loadScenario}
              className="w-full py-4 bg-primary-600 hover:bg-primary-700 text-white font-bold rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              <Icon name="Play" className="w-6 h-6" />
              Start Cascade Challenge
            </button>
          </div>
        )}

        {/* Active Challenge */}
        {scenario && !isComplete && (
          <div className="space-y-6">
            {/* Scenario context */}
            <div className="bg-gradient-to-r from-primary-50 to-secondary-50 p-6 rounded-xl border-2 border-primary-200">
              <h3 className="text-lg font-bold text-gray-900 mb-2">{scenario.name}</h3>
              <p className="text-sm text-gray-700 mb-3">{scenario.scenarioContext}</p>
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <Icon name="User" className="w-4 h-4 text-primary-600" />
                  <span className="font-semibold text-gray-700">Persona:</span>
                  <span className="text-gray-600">{scenario.buyerPersona}</span>
                </div>
              </div>
            </div>

            {/* Progress indicator */}
            <div className="flex items-center justify-center gap-4">
              {[1, 2, 3].map(num => (
                <div key={num} className="flex items-center gap-2">
                  <div className={cn(
                    'w-10 h-10 rounded-full flex items-center justify-center font-bold',
                    num < currentObjection ? 'bg-green-500 text-white' :
                    num === currentObjection ? 'bg-primary-600 text-white' :
                    'bg-gray-200 text-gray-500'
                  )}>
                    {num < currentObjection ? '✓' : num}
                  </div>
                  {num < 3 && (
                    <div className={cn(
                      'w-12 h-1 rounded',
                      num < currentObjection ? 'bg-green-500' : 'bg-gray-200'
                    )} />
                  )}
                </div>
              ))}
            </div>

            {/* Current objection */}
            <div className="bg-white p-6 rounded-xl border-2 border-gray-200">
              <div className="flex items-start gap-3 mb-4">
                <Icon name="MessageSquare" className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-sm font-semibold text-gray-500 mb-1">Objection #{currentObjection}</p>
                  <p className="text-lg font-medium text-gray-900">
                    {currentObjection === 1 ? scenario.objection1 :
                     currentObjection === 2 ? scenario.objection2 :
                     scenario.objection3}
                  </p>
                </div>
              </div>

              {/* Recommended technique hint */}
              <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2">
                  <Icon name="Lightbulb" className="w-4 h-4 text-blue-600" />
                  <p className="text-xs text-blue-800">
                    <strong>Recommended:</strong> {getRecommendedTechnique(currentObjection)?.split('-').map(w => 
                      w.charAt(0).toUpperCase() + w.slice(1)
                    ).join(' ')}
                  </p>
                </div>
              </div>

              {/* Response input */}
              <textarea
                value={responses[currentObjection]}
                onChange={(e) => handleResponseChange(currentObjection, e.target.value)}
                placeholder="Type your response..."
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all resize-none"
                rows={4}
              />

              {/* Submit button */}
              <button
                onClick={() => handleSubmitResponse(currentObjection)}
                disabled={!responses[currentObjection] || responses[currentObjection].trim().length === 0}
                className="mt-4 w-full py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
              >
                {currentObjection < 3 ? 'Next Objection →' : 'Complete Challenge'}
              </button>
            </div>

            {/* Previous responses */}
            {currentObjection > 1 && (
              <div className="space-y-4">
                <h3 className="text-sm font-bold text-gray-700">Previous Responses</h3>
                {[1, 2, 3].filter(num => num < currentObjection).map(num => (
                  <div key={num} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <p className="text-xs font-semibold text-gray-500 mb-2">
                      Objection #{num}: {num === 1 ? scenario.objection1 : scenario.objection2}
                    </p>
                    <p className="text-sm text-gray-700 mb-2">{responses[num]}</p>
                    {detectedTechniques[num] && (
                      <TechniqueDetectionBadge
                        detection={detectedTechniques[num]}
                        executionScore={executionScores[num]}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Results */}
        {scenario && isComplete && (
          <div className="space-y-6">
            {/* Overall score */}
            <div className="bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-xl border-2 border-green-200">
              <div className="text-center">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Challenge Complete!</h3>
                <div className="flex items-center justify-center gap-6 mb-4">
                  <div>
                    <p className="text-5xl font-bold text-green-600">{calculateOverallScore()}</p>
                    <p className="text-sm text-gray-600">Overall Score</p>
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-blue-600">{totalTime}s</p>
                    <p className="text-sm text-gray-600">Total Time</p>
                  </div>
                </div>
                <p className="text-sm text-gray-700">{scenario.successCriteria}</p>
              </div>
            </div>

            {/* All responses */}
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-gray-900">Your Responses</h3>
              {[1, 2, 3].map(num => (
                <div key={num} className="bg-white p-6 rounded-xl border-2 border-gray-200">
                  <p className="text-sm font-semibold text-gray-500 mb-2">
                    Objection #{num}: {num === 1 ? scenario.objection1 : num === 2 ? scenario.objection2 : scenario.objection3}
                  </p>
                  <p className="text-sm text-gray-700 mb-4">{responses[num]}</p>
                  {detectedTechniques[num] && (
                    <TechniqueDetectionBadge
                      detection={detectedTechniques[num]}
                      executionScore={executionScores[num]}
                    />
                  )}
                </div>
              ))}
            </div>

            {/* Coaching tip */}
            <div className="bg-blue-50 p-6 rounded-xl border-2 border-blue-200">
              <div className="flex items-start gap-3">
                <Icon name="GraduationCap" className="w-6 h-6 text-blue-600 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-blue-900 mb-2">Coaching Tip</h4>
                  <p className="text-sm text-blue-800">{scenario.coachingTip}</p>
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex gap-4">
              <button
                onClick={() => setScenario(null)}
                className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-lg transition-colors"
              >
                New Challenge
              </button>
              <button
                onClick={() => navigate('/app/techniques-library')}
                className="flex-1 py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-colors"
              >
                Study Techniques
              </button>
            </div>
          </div>
        )}
      </div></div>
  );
};

export default CascadeChallenge;
